'use client';

/**
 * MessageBlock — renders a single message in the V2 chat conversation.
 *
 * Built from scratch — does NOT use AI Elements Message/MessageContent
 * because their hardcoded group-[.is-user] selectors (bg-secondary,
 * rounded-lg, px-[16px] py-[12px]) fight against Figma styling overrides.
 *
 * Uses AI Elements MessageResponse (Streamdown) for markdown rendering only.
 *
 * Figma 460:6625 (+ FB21 bare-prose decision):
 *   User bubble:      #E36001 bg, white text, rounded 20px with 6px bottom-right
 *   Assistant text:   BARE prose on the canvas — no bubble (owner decision
 *                     FB21: all of Sara's prose reads in one voice, matching
 *                     the RAG path's design-4a answer; shared ASSISTANT_PROSE
 *                     typography from lib/design-tokens.ts)
 *   Tool results:      full-width card, rendered outside bubbles
 */

import React, { memo, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { MessageResponse } from '@/components/ai-elements/message';
import { getToolRenderer } from '@/lib/tool-registry';
import { FadeSlideIn } from '@/components/assistant/fade-slide-in';
import { ContactSummaryCard } from '@/components/assistant/contact-summary-card';
import { HandoffRecapCard } from '@/components/assistant/handoff-recap-card';
import { HandoffTimeoutSubmitted } from '@/components/assistant/handoff-timeout-submitted';
import {
  HANDOFF_STATUS_CHIP_KIND,
  HandoffStatusChip,
} from '@/components/assistant/handoff-status-chip';
import { IdentityAvatar } from '@/components/assistant/primitives/identity-avatar';
import { MessageReactionRow } from '@/components/assistant/message-reaction-row';
import { cn } from '@/lib/utils';
import { ASSISTANT_PROSE } from '@/lib/design-tokens';
import type { Course } from '@/types/course';
import type { RAGData } from '@/types/rag';
import type { FeedbackResponse } from '@/lib/api/feedback';
import type { SupportRequestType } from '@/lib/api/support-requests';
import {
  SendStatus,
  ToolName,
  HandoffEndReason,
  type AgentAttachment,
  type ContactSummary,
  type Message,
} from '@/types/chat';

/**
 * Up-to-2-letter initials for the human-agent bubble's IdentityAvatar.
 * Falls back to 'A' (Art of Living) — defensive only: an AGENT_MESSAGE event
 * always carries an agent_display_name (see useAssistantConnection.ts).
 * Duplicated (not shared) with presence-ribbon.tsx's identical helper — both
 * are tiny, and the two files intentionally have no import relationship.
 */
function getAgentInitials(name?: string): string {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const initials = parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return initials || 'A';
}

/** The one slot every tool-result card renders through. Owning the entrance
 *  here (rather than inside each renderer) means all result cards — current
 *  and future — arrive identically instead of popping into place.
 *  `animate=false` (restored rows) renders statically, per the repo's
 *  reloaded-transcripts-never-replay-entrances invariant (see the
 *  `restored` prop / ContactSummaryCard.animateIn). */
function ToolResultSlot({
  animate,
  children,
}: {
  animate: boolean;
  children: React.ReactNode;
}) {
  const body = <div className="w-full">{children}</div>;
  return animate ? <FadeSlideIn>{body}</FadeSlideIn> : body;
}

// ── Types ─────────────────────────────────────────────────────────

interface MessageBlockProps {
  /** The Message.id, stamped as `data-message-id` on the block's root so
   *  imperative scroll code (ConversationArea's new-turn top anchor,
   *  FB27 #6) can locate this block in the scroller. This is the
   *  CLIENT-visible id (a Realtime item_id, or a client-minted fallback) —
   *  NOT the DB id feedback submissions need. See `dbId` below; the two
   *  must never be conflated (that conflation is exactly the bug the
   *  message-persisted id bridge fixes). */
  messageId?: string;
  /** The server-side `messages.id` (Message.dbId in types/chat.ts), threaded
   *  through to MessageReactionRow — the ONLY id `feedback.message_id`
   *  accepts. Absent until restore-messages.ts stamps it (restored rows) or
   *  the backend's message_persisted frame lands (live rows). Distinct from
   *  `messageId` above on purpose: that one scopes the scroll anchor, this
   *  one scopes feedback — conflating them is what caused live per-message
   *  feedback to 422. */
  dbId?: string;
  userPrompt?: React.ReactNode;
  /** Delivery lifecycle of the user message (from the connection machine's
   *  outbox). queued|sent → "Sending…"; failed → "Not sent · Retry";
   *  acked/undefined → no indicator. */
  userStatus?: SendStatus;
  /** Re-attempt delivery of a failed user message (idempotent — the backend
   *  dedups on the message id). Rendered only when userStatus is failed. */
  onRetry?: () => void;
  agentContent?: React.ReactNode;
  /** Backend-originated in-character notice (e.g., reconnect notice).
   *  Renders centered + muted to read as a system event, not a chat turn.
   *  Superseded by `contactSummary` below when present — the fallback-safety
   *  path renders THIS instead if that's absent/malformed. */
  systemContent?: React.ReactNode;
  /** SYSTEM messages only — the post-submit "Details shared" receipt
   *  (Decision #18, 2026-07-hubspot-native-handoff.md). When present, takes
   *  over the system-notice slot and renders ContactSummaryCard instead of
   *  the plain muted `systemContent` line. */
  contactSummary?: ContactSummary;
  /** SYSTEM messages only — a restored `support_request_submitted` marker
   *  (see restore-messages.ts). When present, takes over the system-notice
   *  slot the same way `contactSummary` does, rendering
   *  HandoffTimeoutSubmitted (the same "we got it" card a LIVE submission
   *  through HandoffTimeoutPanel shows) instead of the plain grey notice. */
  supportRequestSubmitted?: { type: SupportRequestType };
  /** SYSTEM messages only — v3 turn 5 graceful-end recap (Decision: presence
   *  ribbon + recap card design, 2026-07-12). When present, takes over the
   *  system-notice slot the same way `contactSummary` does, rendering
   *  HandoffRecapCard instead of the plain grey system notice. Populated by
   *  page.tsx when it detects the live handoff→ENDED transition and matches
   *  it to this row — see page.tsx's handoffRecapsByMessageId for the
   *  detection logic and its documented limitations. `endReason` is threaded
   *  through so HandoffRecapCard can suppress itself for
   *  HandoffEndReason.TIMEOUT_NO_AGENT (see that component's header). */
  handoffEndedRecap?: {
    agentDisplayName?: string;
    endReason?: HandoffEndReason;
  };
  /** SYSTEM messages only — the "{name} joined the chat" center status chip
   *  (timeout-recovery design re-sync). Takes over the system-notice slot
   *  the same way `contactSummary`/`handoffEndedRecap` do, superseding the
   *  backend's plain grey "joined" notice with HandoffStatusChip's green
   *  treatment. Populated by page.tsx when it detects the live
   *  handoff→CONNECTED transition — see page.tsx's
   *  agentJoinedChipsByMessageId (same claim-by-transition mechanism as
   *  handoffEndedRecap above). */
  agentJoinedChip?: { agentDisplayName?: string };
  /** SYSTEM messages only — a restored tool_call row that never produced a
   *  renderable card (persisted status running/failed/abandoned — see
   *  restore-messages.ts). When present, takes over the system-notice slot
   *  the same way `contactSummary`/`handoffEndedRecap` do, rendering a quiet
   *  inline "didn't finish" chip instead of a plain muted notice. */
  toolOutcome?: Message['toolOutcome'];
  /** True for messages replayed from persistence (history restore). Gates
   *  entrance animations — a reloaded transcript must render statically,
   *  never replay the live expand-in (see ContactSummaryCard.animateIn). */
  restored?: boolean;
  /** HUMAN_AGENT message text (a live HubSpot rep's reply, relayed over the
   *  WS) — rendered in a bubble visually distinct from Sara's, labeled with
   *  the rep's name. */
  humanAgentContent?: string;
  /** Sanitized rich-text HTML for the same message, when the rep sent
   *  formatted text (bold/italic/links/lists). Preferred over
   *  humanAgentContent when present — same Streamdown render pipeline as
   *  assistant content. */
  humanAgentRichText?: string;
  /** Display name of the HubSpot rep who sent humanAgentContent. */
  agentDisplayName?: string;
  /** Files the rep attached — rendered as plain download links (no inline
   *  preview in v1 — Decision #10, 2026-07-hubspot-native-handoff.md). */
  attachments?: AgentAttachment[];
  courseList?: Course[];
  /** find_courses only — the searched zip/city from MessageResult
   *  .searchedLocation; the course panel titles itself with it. */
  courseSearchedLocation?: string;
  ragContent?: RAGData;
  streaming?: boolean;
  onRendered?: () => void;
  /** Needed only for the reaction row below (message-reaction-row.tsx) —
   *  every feedback POST is scoped to a conversation. Omitted by call sites
   *  that never render Sara's bare-prose branch (course/RAG-only blocks). */
  conversationId?: string;
  /** This message's already-recorded feedback, resolved by the caller from
   *  GET .../feedback (see lib/feedback-restore.ts) — lets the reaction row
   *  restore its thumb after a reload instead of starting blank. */
  existingFeedback?: FeedbackResponse;
}

// ── Component ─────────────────────────────────────────────────────

export const MessageBlock = memo(function MessageBlock({
  messageId,
  dbId,
  userPrompt,
  userStatus,
  onRetry,
  agentContent,
  systemContent,
  contactSummary,
  supportRequestSubmitted,
  handoffEndedRecap,
  agentJoinedChip,
  toolOutcome,
  restored,
  humanAgentContent,
  humanAgentRichText,
  agentDisplayName,
  attachments,
  courseList,
  courseSearchedLocation,
  ragContent,
  streaming,
  onRendered,
  conversationId,
  existingFeedback,
}: MessageBlockProps) {
  useEffect(() => {
    onRendered?.();
  }, [
    onRendered,
    agentContent,
    courseList,
    ragContent,
    systemContent,
    contactSummary,
    supportRequestSubmitted,
    handoffEndedRecap,
    agentJoinedChip,
    humanAgentContent,
  ]);

  const ToolRenderer = courseList
    ? getToolRenderer(ToolName.FIND_COURSES)
    : ragContent
      ? getToolRenderer(ToolName.BROWSE_AOL_CONTENT)
      : null;

  // A truthy ragContent with no registered renderer means this route never
  // imported '@/lib/tool-registrations' (a side-effect import, easy to miss
  // when a new route is added) — the card silently disappears while Sara's
  // prose still renders. Loud in dev/test so this class of bug can't recur
  // unnoticed; skipped in prod to avoid console noise for real users.
  if (ragContent && !ToolRenderer && process.env.NODE_ENV !== 'production') {
    console.warn(
      `[MessageBlock] No renderer registered for ${ToolName.BROWSE_AOL_CONTENT} — is '@/lib/tool-registrations' imported on this route?`
    );
  }

  return (
    <div className="flex flex-col gap-[12px]" data-message-id={messageId}>
      {/* ── System notice ── (e.g., reconnect message from the backend) —
          OR the "Details shared" receipt card when this row is a
          contact_summary (Decision #18) — OR the support-request "we got it"
          card when this row is a restored support_request_submitted marker
          — OR the v3 graceful-end recap when page.tsx has matched this row
          to a just-ended handoff — OR the green "{name} joined the chat"
          status chip when page.tsx has matched this row to a
          just-connected handoff — OR a quiet "didn't finish" chip when this
          row is a restored tool_call whose lifecycle never produced a card.
          contactSummary / supportRequestSubmitted / handoffEndedRecap /
          agentJoinedChip / toolOutcome each take over the slot in turn; when
          all are absent (including the fallback-safety case where restore
          mapping couldn't parse a contact_summary/support_request_submitted
          row), the plain muted notice still renders from `content`, so
          nothing is ever silently dropped. (handoff_timeout_offer rows are
          NOT part of this chain and render NOTHING at all: the mapper drops
          their `content` on a successful parse, so `systemContent` is
          undefined and this whole block collapses. They exist only so
          page.tsx can mount HandoffTimeoutPanel at the transcript tail —
          see lib/handoff-timeout-restore.ts and restore-messages.ts's
          branch comment for why staying silent here is correct.)

          handoffEndedRecap only claims the slot when its endReason ISN'T
          TIMEOUT_NO_AGENT — HandoffRecapCard suppresses ITSELF for that
          reason (HandoffTimeoutPanel owns that moment instead, see
          page.tsx's isHandoffTimedOut), but that self-suppression returns
          null from inside the branch this ternary already committed to.
          Checking endReason here too, BEFORE picking the branch, is what
          lets a suppressed recap fall through to the plain `systemContent`
          notice below instead of blanking the row — the personalized
          timeout apology persisted by HandoffRelay._deadline_watchdog lives
          in that plain-notice `content`, and it must render live, not just
          on a later reload (restored rows never claim into
          handoffEndedRecap in the first place, so they always fell through
          correctly — only the LIVE claim path had this bug). */}
      {contactSummary ? (
        <ContactSummaryCard {...contactSummary} animateIn={!restored} />
      ) : supportRequestSubmitted ? (
        <HandoffTimeoutSubmitted type={supportRequestSubmitted.type} />
      ) : handoffEndedRecap &&
        handoffEndedRecap.endReason !== HandoffEndReason.TIMEOUT_NO_AGENT ? (
        <HandoffRecapCard
          agentDisplayName={handoffEndedRecap.agentDisplayName}
          endReason={handoffEndedRecap.endReason}
          animateIn={!restored}
        />
      ) : agentJoinedChip ? (
        <HandoffStatusChip
          kind={HANDOFF_STATUS_CHIP_KIND.AGENT_JOINED}
          agentDisplayName={agentJoinedChip.agentDisplayName}
          animateIn={!restored}
        />
      ) : toolOutcome ? (
        // Quiet, timeline-adjacent — deliberately NOT styled like an error
        // banner (no border/surface/box): all three persisted statuses
        // (running/failed/abandoned) collapse to the same copy, since a
        // RESTORED "running" tool means the session died mid-call — there is
        // no retry affordance, just an honest "this didn't finish".
        <div
          role="status"
          aria-live="polite"
          className="mx-auto flex items-center gap-1.5 px-[16px] py-1"
        >
          <AlertCircle aria-hidden className="size-[14px] text-[#8a7c68]" />
          <div className="font-['Work_Sans'] text-[12.5px] text-[#8a7c68]">
            Sara&apos;s search didn&apos;t finish
          </div>
        </div>
      ) : (
        systemContent && (
          <div
            role="status"
            aria-live="polite"
            className={cn(
              'mx-auto max-w-[85%] rounded-[12px] px-[16px] py-2 text-center',
              "font-['Work_Sans'] text-[13px] leading-relaxed text-[#7a7e90]",
              'break-words',
              'border-[1px] border-[#f2f0ee] bg-white/60 backdrop-blur-sm'
            )}
          >
            {systemContent}
          </div>
        )
      )}

      {/* ── User message ── */}
      {userPrompt && (
        <div className="flex flex-col items-end gap-1">
          <div
            className={cn(
              'max-w-[85%] sm:max-w-[60%]',
              // T9a user bubble: sharp corner points INTO the right margin
              // (bottom-right), mirroring the assistant side — 20/20/6/20.
              'rounded-tl-[20px] rounded-tr-[20px] rounded-br-[6px] rounded-bl-[20px]',
              'bg-[#e36001] px-[18px] py-[12px]',
              'text-[15px] font-medium leading-normal text-white',
              "font-['Work_Sans']",
              // Break an unbroken string (a pasted URL, a "Yooooo…" run) so it
              // wraps inside the bubble instead of blowing past max-w and
              // overflowing the chat column. overflow-wrap is inherited, so
              // this also covers any MessageResponse children.
              'break-words'
            )}
          >
            {userPrompt}
          </div>

          {/* Delivery indicator — only for in-flight or failed sends. acked /
              legacy messages render nothing (the quiet default). */}
          {(userStatus === SendStatus.QUEUED ||
            userStatus === SendStatus.SENT) && (
            <span
              role="status"
              aria-live="polite"
              className="pr-1 font-['Work_Sans'] text-[12px] text-[#7a7e90]"
            >
              Sending…
            </span>
          )}
          {userStatus === SendStatus.FAILED && (
            <span className="flex items-center gap-1.5 pr-1 font-['Work_Sans'] text-[12px]">
              <span role="alert" className="text-[#b3261e]">
                Not sent
              </span>
              {onRetry && (
                <>
                  <span aria-hidden className="text-[#7a7e90]">
                    ·
                  </span>
                  <button
                    type="button"
                    onClick={onRetry}
                    className="font-medium text-design-orange underline-offset-2 hover:underline"
                  >
                    Retry
                  </button>
                </>
              )}
            </span>
          )}
        </div>
      )}

      {/* ── Human agent message (live HubSpot rep) ──
          Left-aligned like Sara's bubble but visually distinct — v3 turn 5
          replaces the blue-tinted surface + plain name label with a neutral
          warm-palette card (bg white / border-[1px] #f0e7da, matching the rest of
          the v3 chrome) + an IdentityAvatar (navy initials, green online dot)
          so a user never confuses "Sara" with "a human just took over"
          mid-conversation. */}
      {humanAgentContent !== undefined && (
        <div className="flex justify-start">
          <div
            className={cn(
              'max-w-[92%] sm:max-w-[85%]',
              'rounded-[20px] bg-white px-[20px] py-[12px]',
              'text-[15px] leading-relaxed text-[#31364e]',
              "font-['Work_Sans']",
              'border-[1px] border-[#f0e7da]',
              // Wrap an unbroken run (URL / "Yooooo…") instead of overflowing
              // the bubble — inherited into the MessageResponse child too.
              'break-words'
            )}
          >
            {agentDisplayName && (
              <div className="mb-2 flex items-center gap-2">
                <IdentityAvatar
                  initials={getAgentInitials(agentDisplayName)}
                  size={20}
                  online
                />
                <span className="text-[12.5px] font-semibold text-[#8a7c68]">
                  {agentDisplayName} · Art of Living support
                </span>
              </div>
            )}
            <MessageResponse>
              {humanAgentRichText || humanAgentContent}
            </MessageResponse>
            {attachments && attachments.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                {attachments.map((a) => (
                  <a
                    key={a.file_id}
                    href={a.url ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'text-[13px] font-medium text-design-blue underline-offset-2 hover:underline',
                      !a.url && 'pointer-events-none opacity-50'
                    )}
                  >
                    {a.name || 'Download attachment'}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tool results — full-width ── Every tool-result card enters
          through ToolResultSlot: the entrance animation is a property of
          the SLOT, not of each renderer, so any future registered tool gets
          the same arrival for free (and RagAnswerBlock — the composed RAG
          flow that bypasses MessageBlock — applies the same FadeSlideIn to
          its media, keeping both paths identical). */}
      {courseList && ToolRenderer && (
        <ToolResultSlot animate={!restored}>
          <ToolRenderer
            courses={courseList}
            searchedLocation={courseSearchedLocation}
          />
        </ToolResultSlot>
      )}

      {ragContent && ToolRenderer && (
        <ToolResultSlot animate={!restored}>
          <ToolRenderer data={ragContent} />
        </ToolResultSlot>
      )}

      {/* ── Assistant text — bare prose on the canvas, no bubble (FB21).
          Same ASSISTANT_PROSE typography as RagAnswerBlock's answer, so a
          course-only or plain turn reads in the same voice as a RAG turn
          instead of dropping into white-bubble chrome. */}
      {(agentContent || streaming) && (
        // `group` so MessageReactionRow's hover-to-reveal (below) fires when
        // the pointer is anywhere over the prose, not just over the row's
        // own 32px-tall strip — see that component's header comment.
        <div className={cn('group w-full', ASSISTANT_PROSE)}>
          {typeof agentContent === 'string' ? (
            <MessageResponse
              isAnimating={streaming}
              caret={streaming ? 'block' : undefined}
            >
              {agentContent}
            </MessageResponse>
          ) : (
            <>
              {agentContent}
              {streaming && (
                <span className="ml-1 animate-pulse text-[#e36001]">▍</span>
              )}
            </>
          )}

          {/* Reaction row — only once the answer has actually finished
              streaming (rating a still-arriving answer makes no sense) and
              only when this row has a durable message id + conversation to
              scope the feedback to. */}
          {!streaming &&
            messageId &&
            conversationId &&
            typeof agentContent === 'string' &&
            agentContent.trim().length > 0 && (
              <MessageReactionRow
                conversationId={conversationId}
                dbId={dbId}
                text={agentContent}
                existingFeedback={existingFeedback}
              />
            )}
        </div>
      )}
    </div>
  );
});
